import { useCallback, useEffect, useRef, useState } from 'react'

// Delays before the second and third load of a stored reference image. The
// server deletes a reference 7 days after upload and a deleted object 404s on
// every request, while a network or CDN blip usually loads on the next try, so
// a reference is only shown as expired once all three loads have failed.
export const REFERENCE_IMAGE_RETRY_DELAYS_MS = [2000, 5000]
const MAX_REFERENCE_IMAGE_LOADS = REFERENCE_IMAGE_RETRY_DELAYS_MS.length + 1

// A retry needs a fresh request rather than the browser's cached failure.
export function referenceImageRetryUrl(url, attempt) {
  if (!attempt) return url
  return `${url}${url.includes('?') ? '&' : '?'}retry=${attempt}`
}

const isStoredReference = (reference) => reference.status !== 'uploading' && Boolean(reference.id)

// Load state of the composer's stored reference images, as
// { [id]: { url, failures, attempt, phase } } with phase 'image' (an <img> is
// mounted for `attempt`), 'retrying' (waiting for the next attempt) or
// 'expired'. References that never failed have no entry; one that loaded after
// a retry keeps its attempt with no failures. An entry only applies
// while its URL is the reference's current URL. Uploading previews are never
// tracked.
export function useReferenceImageRetry(referenceImages) {
  const [loads, setLoads] = useState({})
  const loadsRef = useRef(loads)
  const timersRef = useRef(new Map())

  const commit = useCallback((next) => {
    loadsRef.current = next
    setLoads(next)
  }, [])

  const clearTimer = useCallback((id) => {
    const timer = timersRef.current.get(id)
    if (timer === undefined) return
    clearTimeout(timer)
    timersRef.current.delete(id)
  }, [])

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach((timer) => clearTimeout(timer))
      timers.clear()
    }
  }, [])

  // Forget a reference that was removed or whose URL changed, with its timer.
  useEffect(() => {
    const urls = new Map(referenceImages.filter(isStoredReference).map((reference) => [reference.id, reference.url]))
    const next = {}
    let changed = false
    Object.entries(loadsRef.current).forEach(([id, entry]) => {
      if (urls.get(id) === entry.url) {
        next[id] = entry
        return
      }
      clearTimer(id)
      changed = true
    })
    if (changed) commit(next)
  }, [clearTimer, commit, referenceImages])

  // Back online: request every image that is still failing (waiting for a
  // retry or expired) again, from scratch. Entries in phase 'image' have an
  // <img> mounted, whether it loaded or is still loading, and are left alone.
  useEffect(() => {
    const handleOnline = () => {
      const failing = Object.entries(loadsRef.current)
        .filter(([, entry]) => entry.phase === 'retrying' || entry.phase === 'expired')
      if (!failing.length) return
      const next = { ...loadsRef.current }
      failing.forEach(([id, entry]) => {
        clearTimer(id)
        next[id] = { url: entry.url, failures: 0, attempt: entry.attempt + 1, phase: 'image' }
      })
      commit(next)
    }
    window.addEventListener('online', handleOnline)
    return () => window.removeEventListener('online', handleOnline)
  }, [clearTimer, commit])

  const imageState = (reference) => {
    const entry = isStoredReference(reference) ? loads[reference.id] : null
    if (!entry || entry.url !== reference.url) return { phase: 'image', attempt: 0, src: reference.url }
    return { phase: entry.phase, attempt: entry.attempt, src: referenceImageRetryUrl(reference.url, entry.attempt) }
  }

  // Asked for by the person (a "Try again" on a failed thumbnail): the image
  // is requested again from scratch, whatever its state.
  const retry = (reference) => {
    if (!isStoredReference(reference)) return
    const { id, url } = reference
    const current = loadsRef.current[id]
    const attempt = current?.url === url ? current.attempt + 1 : 1
    clearTimer(id)
    commit({ ...loadsRef.current, [id]: { url, failures: 0, attempt, phase: 'image' } })
  }

  const handleError = (reference) => {
    if (!isStoredReference(reference)) return
    const { id, url } = reference
    const current = loadsRef.current[id]
    const entry = current?.url === url ? current : { url, failures: 0, attempt: 0, phase: 'image' }
    if (entry.phase !== 'image') return
    const failures = entry.failures + 1
    clearTimer(id)
    if (failures >= MAX_REFERENCE_IMAGE_LOADS) {
      commit({ ...loadsRef.current, [id]: { ...entry, failures, phase: 'expired' } })
      return
    }
    commit({ ...loadsRef.current, [id]: { ...entry, failures, phase: 'retrying' } })
    timersRef.current.set(id, setTimeout(() => {
      timersRef.current.delete(id)
      const latest = loadsRef.current[id]
      if (latest?.url !== url || latest.phase !== 'retrying') return
      commit({ ...loadsRef.current, [id]: { ...latest, phase: 'image', attempt: latest.attempt + 1 } })
    }, REFERENCE_IMAGE_RETRY_DELAYS_MS[failures - 1]))
  }

  // A load clears the failure count but keeps the attempt, so the <img> that
  // just loaded is not swapped back to the original (possibly cached) URL.
  const handleLoad = (reference) => {
    if (!isStoredReference(reference)) return
    const current = loadsRef.current[reference.id]
    if (!current || current.url !== reference.url || (current.failures === 0 && current.phase === 'image')) return
    clearTimer(reference.id)
    commit({ ...loadsRef.current, [reference.id]: { ...current, failures: 0, phase: 'image' } })
  }

  return { imageState, handleError, handleLoad, retry }
}
